import { useState, useEffect, useContext, useRef, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { Row, Col, Button, Rate, Skeleton, Modal } from 'antd';
import { useInView } from 'react-intersection-observer';

import { ProductInfoSection } from 'components/productInfoSection';
import { getCountryFromMarket } from 'utils/translations';
import { setCookie, obtainVoucherPrices, getCookie, COOKIES, eraseCookie, getLang, getMarket, handleStorage, getFixedPrice } from 'utils/helpers';

import { addToBag, getCrossSelling, getProduct, getCart, retrieveBag, getRokuStock } from 'actions/products';
import { useI18n } from 'context/i18n';
import { userContext } from 'context/user';
import { dataLayerCartClick, dataLayerCartClickV2, dataLayerHandleEvent } from 'utils/dataLayers';

import { MAX_BAG_LENGTH, FAMILIES, FIXED_BLOCKED_SKUS } from 'utils/constants/products';
import { BAZAAR_VOICE_MARKETS } from 'utils/constants/ux';
import { AUTO_BONDS } from 'utils/constants/cookies';
import { SIZE_GUIDE_WITH_MODELS } from 'utils/constants/sizes';

import { setBagCookies } from 'utils/bag';
// import { ProductShare } from 'components/productShare';
import { useSunnei } from 'context/product-sheet';
import { useIsIpad } from 'hooks/useIsIpad';
import { ProductBrands } from './productBrands';
import { ProductBreadcrumbs } from './productBreadcrumbs';
import { TargetLinks } from './targetLinks';
import { handleAddToBag } from 'context/bag';
import { useIsMounted } from 'hooks';
import { ProductCard } from 'components/product/product-card';

import { debugAPI } from 'actions/general';
import styles from './style.module.css';
import { getDomainValue } from 'utils/domain';

const SizesList = dynamic(() => import('components/sizesList'));
const ONESVG = dynamic(() => import('components/iconsSVG/one').then((mod) => mod.ONESVG));
const RateSVG = dynamic(() => import('components/iconsSVG/rate').then((mod) => mod.RateSVG));

const SizesGuide = dynamic(() => import('components/sizesGuide').then((mod) => mod.default));
const GiftCard: any = dynamic(() => import('components/giftCard').then((mod) => mod.default) as any);
const NotifyMeButton = dynamic(() => import('components/notifyMeButton'));
const AddToBagBtn = dynamic(() => import('./addToBagBtn').then((mod) => mod.AddToBagBtn));
// const AddToWishlistBtn = dynamic(() => import('./addToWishlistBtn').then((mod) => mod.default));
const AfterpayButton = dynamic(() => import('components/checkout/afterpayButton').then((mod) => mod.default));
const KboixMidsoles = dynamic(() => import('components/pdp/midsoles').then((mod) => mod.KboixMidsoles));
const DispoModal = dynamic(() => import('components/dispoModal').then((mod) => mod.DispoModalWithButton));

export function ProductPurchaseCol({
  productSheet,
  averageRate,
  hasReviews,
  locale,
  market,
  isCamperOne = false,
  isGiftCard = false,
  isSock = false,
  onClickReviews = () => {},
  isFullScreen = false,
  recalculating = false,
  voucherApplied = false,
  voucherPriceText,
  isRoku = false,
  isDimReady = false,
  usrhashValue = null,
}) {
  const {
    t,
    bagLength,
    profileData,
    setBagLength,
    bagHasMembersOnlyProduct,
    bag,
    setBag,
    setRecommendedProductsForBag,
    setPromoLabel,
    setPromoLabelType,
    storeData,
    disabledProducts,
  } = useI18n();
  const isMounted = useIsMounted();

  const { userData, increaseShouldRecalculateVouchers, contextFilters, activatedPrevouchers } = useContext(userContext);
  const { hasSunneiConfig, selectedSunneiShoes, sunneiInfo, setSelectedSize: setSunneiSize, firstProductHasStock, secondProductHasStock } = useSunnei();
  const { config } = profileData;
  const showFreeShipping = config?.show_free_shipping || false;
  const showGiftPurchase = config?.show_gift_purchase || false;
  const { ref, inView } = useInView({ threshold: 1 });
  const availablePrice = productSheet.prices && productSheet.prices?.current && productSheet?.notifyMeType !== 'OUT';
  const { isAccessory, sizes, code, prices, camperlab, notifyMe } = productSheet;
  const isAvailableAccesory = isAccessory && availablePrice && sizes && sizes.length > 0 ? sizes.filter((size) => size.available).length > 0 : false;
  const isMembersOnly = productSheet?.membersOnly || false;
  const isUniqueSize = sizes.length === 1;
  const defaultSelectedSizeValue = (isAccessory && isAvailableAccesory) || isUniqueSize ? (sizes[0]?.value ?? ' ') : undefined;
  const hasMidsoles = Array.isArray(productSheet.kboixData.midsoles) && productSheet.kboixData.midsoles.length > 0;

  const [isMouseOverCartBtn, setIsMouseOverCartBtn] = useState(false);
  const [cartError, setCartError] = useState(false);
  const [selectedSize, setSelectedSize] = useState(isAccessory && isAvailableAccesory ? (sizes[0]?.value ?? ' ') : undefined);
  const [loading, setLoading] = useState(false);
  const [modalMembersOnlyVisible, setModalMembersOnlyVisible] = useState(true);
  const [showRokuWarning, setShowRokuWarning] = useState(false);
  const [showSunneiWarning, setShowSunneiWarning] = useState(false);
  const [failedSunneis, setFailedSunneis] = useState([]);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [defaultSelectedSize, setDefaultSelectedSize] = useState(defaultSelectedSizeValue);
  const [failedRokus, setFailedRokus] = useState([]);
  const reviewsCount = hasReviews ? productSheet.reviewStatistics?.TotalReviewCount : 0;
  const country = getCountryFromMarket(market);
  const limitedSizes = sizes && sizes.filter((size) => size.quantity === 1).map((item) => item.value);
  const selectedSizeItem = sizes && sizes.filter((size) => size?.value === selectedSize);
  const selectedSizePrice = selectedSizeItem.length && selectedSizeItem[0].price?.current;
  const totalPrice = prices !== null ? selectedSizePrice || prices?.current || prices?.previous : null;
  const availableSizes = sizes.filter((item) => item.available).map((size) => size.value);
  const isBuyingDisabled = disabledProducts === code || FIXED_BLOCKED_SKUS.includes(code);
  const [skusWithoutStock, setSkusWithoutStock] = useState(null);
  const [selectedMidsoles, setSelectedMidsoles] = useState(() => {
    return productSheet.kboixData?.midsoles?.filter((sole) => sole.selected && sole.sizes.some((s) => s.available)) ?? [];
  });
  const appmode = useIsIpad();
  const titleRef = useRef(null);

  // si seteamos directamente el showSizeModal a true dependiendo de las variables da un error de hidratación
  useEffect(() => {
    if ((isRoku || hasSunneiConfig) && !selectedSize) {
      setShowSizeModal(true);
    }
  }, [isRoku, hasSunneiConfig]);

  const handleGetRokuStock = async (size) => {
    const partsWithoutStock = await getRokuStock({ context: undefined, profile: locale, size });

    if (partsWithoutStock) {
      // request OK
      if (Array.isArray(partsWithoutStock)) {
        setSkusWithoutStock(partsWithoutStock);
      }
    }
  };

  const handleSelectSize = async (size) => {
    setSelectedSize(size);
    setSunneiSize(size);

    // hay que controlar que las midsoles no esten seleccionadas si no tienen stock;
    setSelectedMidsoles((prev) => {
      return prev.filter((sole) => sole.sizes.find((item) => item.value === size)?.available === true);
    });

    // puesto que productSheet no tiene control sobre selectedSize
    // necesito hacer esto para leer selectedSize mientras calculo vouchers
    // así puedo cambiar los perSize una vez se selecciona la talla
    window.selectedSizeForVoucher = size;
    increaseShouldRecalculateVouchers();

    setCartError(false);

    dataLayerHandleEvent({
      event: 'item_size_selection',
      event_category: 'product detail',
      event_detail_1: 'size selection',
      event_detail_2: size,
    });

    if (isRoku) {
      await handleGetRokuStock(size);
    }
  };

  const addToCartHandler = async ({ payload, cookiesBagId, showJustAdded }) => {
    const cookiesMarket = getCookie(COOKIES.BAG_MARKET);
    const cookiesShipment = getCookie(COOKIES.SHIPMENT);
    const cookiesBonds = getCookie(COOKIES.BONDS);
    const cookiesZip = getCookie(COOKIES.ZIP);
    const cookiesEmployeeId = getCookie(COOKIES.EMPLOYEE_ID);
    const cookiesPackstation = getCookie(COOKIES.PACKSTATION);
    const cookiesCtr = getCookie(COOKIES.IS_CTR);
    if (cookiesMarket !== market) {
      payload.hashOrderId = '';
      if (cookiesBagId) {
        eraseCookie(COOKIES.BAG);
        eraseCookie(COOKIES.BAG_DATE);
      }
      if (cookiesZip) {
        eraseCookie(COOKIES.ZIP);
      }
      if (cookiesShipment) {
        eraseCookie(COOKIES.SHIPMENT);
      }
      if (cookiesMarket !== null && cookiesBonds) {
        eraseCookie(COOKIES.BONDS, COOKIES.DOMAIN);
        eraseCookie(COOKIES.AUTO_BONDS);
      }
      if (cookiesEmployeeId) {
        eraseCookie(COOKIES.EMPLOYEE_ID);
      }
      if (cookiesPackstation) {
        eraseCookie(COOKIES.PACKSTATION);
      }
      if (cookiesCtr) {
        eraseCookie(COOKIES.IS_CTR);
      }
    }

    const [result, crossSellingProducts] = await Promise.all([addToBag({ ...payload }), getCrossSelling({ profile: locale, productId: payload.id })]);
    let updatedPromoLabel = null;
    let updatedPromoLabelType = null;
    const previousGridUrl = sessionStorage.getItem('gridUrl');

    if (productSheet?.baseUrl && previousGridUrl === undefined) {
      const [domain, fullUrl] = productSheet?.baseUrl?.split(locale);

      try {
        const splitUrl = fullUrl.split('/').filter(Boolean);
        const gridUrl = splitUrl.slice(0, splitUrl.length - 1).join('/');
        handleStorage('setItem', 'gridUrl', `${domain}${locale}/${gridUrl}`);
      } catch (e) {
        // en caso de que algo fallase mandamos a la home
        handleStorage('setItem', 'gridUrl', `${domain}${locale}/`);
      }
    }

    dataLayerCartClick({ ...payload, productSheet }, result, false, bag);
    if (result.hashOrderId) {
      setBagCookies({ hash: encodeURI(result.hashOrderId), market });

      const { sapActive, extendedBlockSAP, shoppingCart } = result;
      if (!sapActive && !extendedBlockSAP) {
        // can't pay
        window.location.assign(`${window.location.origin}/${locale}/pedido_offline`);
      } else {
        if (extendedBlockSAP) {
          // can pay
          setCookie(COOKIES.SHIPMENT, '01', undefined, COOKIES.DOMAIN);
        }
        if (!showJustAdded) {
          window.location.assign(`${window.location.origin}/${locale}/bolsa`);
        }
        if (showJustAdded) {
          const { price } = shoppingCart[0]; // siempre es una array con un solo item
          obtainVoucherPrices({ ...shoppingCart[0], prices: price }, t, locale, contextFilters, activatedPrevouchers, false, true);
          const newBag = bag ? JSON.parse(JSON.stringify(bag)) : [];
          // handleStorage('setItem', 'justAddedItem', JSON.stringify({ ...result.shoppingCart[0], membersOnly: productSheet?.membersOnly }));
          setBag(() => {
            const n = [{ ...result.shoppingCart[0], membersOnly: productSheet?.membersOnly }, ...newBag];
            return n;
          });
          setBagLength(() => {
            const newLength = bagLength + 1;
            return newLength;
          });

          setRecommendedProductsForBag((prev) => [...crossSellingProducts, ...prev].map((item, i) => ({ ...item, order: i })));

          if (showFreeShipping) {
            updatedPromoLabel = result?.freeShippingLabel || null;
          }
          // let promoLabelObtained = false;
          if (showGiftPurchase) {
            updatedPromoLabel = result?.gwplabel || result?.freeShippingLabel || null;
            if (result?.gwplabelObtained && result?.gwplabelObtained !== '') {
              updatedPromoLabel = result?.gwplabelObtained;
              // promoLabelObtained = true;
              updatedPromoLabelType = 'obtained';
            }
          }
          setLoading(false);
          setPromoLabel(updatedPromoLabel);
          setPromoLabelType(updatedPromoLabelType);
        }
      }
      // [TODO]: que quiten la cache de bolsa.json en integra
      // router.push('/bolsa');
    } else {
      setLoading(false);

      try {
        await debugAPI({
          profile: locale,
          payload,
          response: result,
          url: window.location.href,
        });
      } catch (e) {
        console.error(e);
      }
      // [TODO]: handle error
      // eslint-disable-next-line no-alert
      alert('No se puede procesar el pedido en este momento');
    }
  };

  const handleClickAddToCartBtn = async (showJustAdded = false, userIdentified = false) => {
    if (isBuyingDisabled) return;
    const isUserIdentified =
      getCookie(COOKIES.SELLIGENT_USRHASH) || getCookie(COOKIES.MEMBERS_ONLY_SUBSCRIBED) || userIdentified || bagHasMembersOnlyProduct || userData !== null;

    setCartError(false);

    if (!availableSizes.includes(selectedSize)) {
      const message = !isAccessory ? 'elige.talla' : 'producto.nodisponible';
      setCartError(t('ficha.producto', message));
      // check if window is less than 768px
      const mobileWindow = window.innerWidth < 768;
      if (!isAccessory && mobileWindow) {
        if (titleRef.current) {
          // scroll item to the top of the screen
          titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else if (getDomainValue({ nnormal: productSheet.kboix === true })) {
      // must select at least two soles to purchase kboix product
      // primero checkeamos que haya suelas suficientes en el array.
      const thumbnailDefault = productSheet.imageBag;
      const inspirationCode = productSheet.code;
      const kboixMin = productSheet.kboixData.minExtra;
      // la segunda parte del if es si es una suela sola en la pdp
      if (selectedMidsoles.length < kboixMin && productSheet.kboixData.midsoles.length > 0) {
        setCartError(t('kboix.soles', 'error.min', 'You must select at least two soles'));
        return;
      }
      try {
        setLoading(true);
        const mainId = productSheet.kboixData.sku;
        if (!mainId) {
          // no deberia de pasar nunca
          setCartError(t('kboix.soles', 'error.missing', 'Somthing went wrong'));
          return;
        }

        const midsolesForCart = selectedMidsoles.reduce((acc, sole) => {
          const size = sole.sizes.find((size) => size.value === selectedSize);
          if (size.available) {
            const solePayload: {
              id: string;
              size: string;
              thumbnail: string;
              parent?: string;
            } = { id: sole.id, size: selectedSize, thumbnail: thumbnailDefault.replace(inspirationCode, sole.id) };
            if (acc.length < kboixMin) {
              solePayload.parent = mainId;
            }
            acc.push(solePayload);
          }
          return acc;
        }, []);

        const productsToAdd = [
          {
            id: mainId,
            size: selectedSize,
            thumbnail: thumbnailDefault.replace(inspirationCode, mainId),
          },
          ...midsolesForCart,
        ];
        const { cartResult, crossResult } = await handleAddToBag({
          mainId: mainId,
          payload: { products: productsToAdd },
          allMandatory: true,
          checkStock: true,
          locale,
        });
        if (!isMounted()) return;
        //TODO: todo esto siguiente podemos cuando empezemos a refactorizar el carrito (mover a un contexto especifico)
        let updatedPromoLabel = null;
        let updatedPromoLabelType = null;

        if (cartResult.hashOrderId) {
          const addedIds = productsToAdd.map((item) => item.id);
          dataLayerCartClickV2({ data: cartResult.shoppingCart, addedIds, bagId: cartResult.hashOrderId, currency: profileData?.currencySap });
          setBagCookies({ hash: encodeURI(cartResult.hashOrderId), market });

          const { sapActive, extendedBlockSAP, shoppingCart } = cartResult;
          if (!sapActive && !extendedBlockSAP) {
            // can't pay
            window.location.assign(`${window.location.origin}/${locale}/pedido_offline`);
            return;
          }
          if (extendedBlockSAP) {
            // can pay
            setCookie(COOKIES.SHIPMENT, '01', undefined, COOKIES.DOMAIN);
          }
          if (!showJustAdded) {
            window.location.assign(`${window.location.origin}/${locale}/bolsa`);
            return;
          } else {
            const price = shoppingCart.find((cartItem) => cartItem.productId === productSheet.code)?.price;
            if (price) {
              obtainVoucherPrices({ ...shoppingCart[0], prices: price }, t, locale, contextFilters, activatedPrevouchers, false, true);
            }
            setBag(shoppingCart);
            setBagLength(shoppingCart.length);

            setRecommendedProductsForBag((prev) => [...crossResult, ...prev].map((item, i) => ({ ...item, order: i })));

            if (showFreeShipping) {
              updatedPromoLabel = cartResult?.freeShippingLabel || null;
            }
            // let promoLabelObtained = false;
            if (showGiftPurchase) {
              updatedPromoLabel = cartResult?.gwplabel || cartResult?.freeShippingLabel || null;
              if (cartResult?.gwplabelObtained && cartResult?.gwplabelObtained !== '') {
                updatedPromoLabel = cartResult?.gwplabelObtained;
                // promoLabelObtained = true;
                updatedPromoLabelType = 'obtained';
              }
            }
            setLoading(false);
            setPromoLabel(updatedPromoLabel);
            setPromoLabelType(updatedPromoLabelType);
          }
        }
      } catch (error) {
        //TODO: handle Error;
        if (!isMounted()) return;
        setCartError(error?.message || 'No se puede procesar el pedido en este momento');
      }
    } else if (bagLength >= MAX_BAG_LENGTH) {
      setCartError(
        t('ficha.producto', 'alcanzado.limite.bolsa', 'The product could not be added. You have reached the limit of products in your shopping cart.'),
      );
    } else if (isMembersOnly && !isUserIdentified) {
      window.showMembersOnlyModal?.('pdp_members_only_popup', undefined, undefined, async () => {
        await handleClickAddToCartBtn(true, true);
      });
    } else if (hasSunneiConfig) {
      /**
       * Añadir a la bolsa en sunnei.
       * Primero necesitamos checkear que para la talla elegida tengamos stock de los dos productos. Si no hay stock de alguno, necesitamos bloquear la compra con un modal. Esto difiere de Roku en que lo hacemos antes de intentar añadir a la bolsa.
       * Ahora los Sunnei se compran en pares, por lo que necesitamos añadir el primero y luego el segundo.
       * Si falla el primero, no añadimos el segundo.
       * Si falla el segundo, quitamos el primero.
       */
      const sunneisWithoutStock = [];
      if (!firstProductHasStock) {
        sunneisWithoutStock.push(sunneiInfo.products[selectedSunneiShoes[0]]);
      }
      if (!secondProductHasStock) {
        sunneisWithoutStock.push(sunneiInfo.products[selectedSunneiShoes[1]]);
      }
      if (sunneisWithoutStock.length > 0) {
        setFailedSunneis(sunneisWithoutStock);
        setShowSunneiWarning(true);
        return;
      }

      setLoading(showJustAdded);
      // separacion
      const addMultiplePayload = [
        {
          sku: selectedSunneiShoes[0],
          size: selectedSize,
          sizeLabel: selectedSize,
          price: sunneiInfo.products[selectedSunneiShoes[0]].prices,
          gender: sunneiInfo.products[selectedSunneiShoes[0]].targetId,
          checkStock: false,
        },
        {
          sku: selectedSunneiShoes[1],
          size: selectedSize,
          sizeLabel: selectedSize,
          price: sunneiInfo.products[selectedSunneiShoes[1]].prices,
          gender: sunneiInfo.products[selectedSunneiShoes[1]].targetId,
          checkStock: false,
        },
      ];
      const addToCartMultipleResult = await window.addToCartMultiple(addMultiplePayload, true, bag);
      const bagID = getCookie(COOKIES.BAG);

      // todo el check posterior puede que nunca se llame, pero lo mantenemos por si falla la api
      try {
        const failed = !addToCartMultipleResult[0];
        let succeededProducts = addToCartMultipleResult[1]?.filter((item) => item.status === 'OK');

        if (failed) {
          const failedProducts = addToCartMultipleResult[1].filter((item) => item.status !== 'OK');
          const failedSunneisWithInfo = [];
          const getProductPromises = [];

          failedProducts.forEach((failedItem) => {
            getProductPromises.push(
              getProduct({
                context: undefined,
                lang: locale,
                productCode: failedItem.sku,
                target: productSheet.realTarget || productSheet.target,
              }).then((productInfo) => {
                if (productInfo?.productSheet) {
                  failedSunneisWithInfo.push(productInfo.productSheet);
                }
              }),
            );
          });

          await Promise.all(getProductPromises);
          // quitar del carrito los que hayan funcionado
          // hacemos un get cart primero
          // quitamos todos los que tengamos en la lista que coincidan

          setFailedSunneis(failedSunneisWithInfo);

          if (succeededProducts?.length > 0) {
            const currentBag = await retrieveBag({
              context: undefined,
              payload: {
                hashOrderId: bagID,
                profile: {
                  countryId: market,
                  languageId: getLang(locale),
                },
              },
            });

            const productsCurrentlyInBag = currentBag?.shoppingCart;

            if (productsCurrentlyInBag?.length > 0) {
              const newBag = [...productsCurrentlyInBag].filter((item) => {
                // devolver false para los que esten en succeeded
                const successfulProduct = succeededProducts.find((succeededItem) => succeededItem.sku === item.productId && succeededItem.size === item.size);

                if (successfulProduct !== undefined) {
                  // encontrado el producto, lo quitamos de la lista y filtramos
                  succeededProducts = succeededProducts.filter(
                    (itemToCheck) => !(itemToCheck.sku === successfulProduct.sku && itemToCheck.size === successfulProduct.size),
                  );
                  return false;
                }

                return true;
              });

              const employeeId = getCookie(COOKIES.EMPLOYEE_ID) || '';
              const zipCode = getCookie(COOKIES.ZIP) || '';
              const voucher = getCookie(COOKIES.BONDS) || '';
              const tax = zipCode && zipCode !== '' ? { zipcode: zipCode } : {};

              await getCart({
                context: undefined,
                lang: locale,
                products: newBag,
                hashOrderId: bagID,
                employeeId,
                bondValue: voucher,
                tax,
                withPaymentMethods: undefined,
                cookies: undefined,
                origin: undefined,
              });
            }
          }

          // await handleGetRokuStock(selectedSize);
        }

        setShowSunneiWarning(failed);

        const updateForBag = [...bag, ...succeededProducts.map((item) => item.shoppingCart?.[0])];

        setBag(updateForBag);
        setBagLength(updateForBag.length);
      } catch (e) {
        console.error(e);
      }

      setLoading(false);
    } else {
      const cookiesBagId = getCookie(COOKIES.BAG);
      setLoading(showJustAdded);
      const sizeLabel = !isAccessory ? sizes.filter((size) => size?.value === selectedSize)[0]?.label : selectedSize;
      const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
      const voucher = getCookie(COOKIES.BONDS) || '';
      const voucherDiscount = autoVoucher && autoVoucher === AUTO_BONDS.APPLY;
      let pricesPayload = prices;
      const discountStrToNumber = prices?.discountStr ? Number(prices?.discountStr?.replace(/\D/g, '')) : 0;
      if (discountStrToNumber > prices.discount) {
        // tenemos que enviar el discount que mas beneficie
        pricesPayload.discount = discountStrToNumber;
      }

      if (prices.pricePerSize) {
        const sizeFound = selectedSize ? sizes.find((size) => size?.value === selectedSize) : null;

        if (sizeFound) {
          pricesPayload = sizeFound?.price;
        }
      }

      const payload: {
        id: string;
        price: object;
        profile: object;
        size: string;
        sizeLabel: string;
        gender: string;
        thumbnail: string;
        voucher?: string;
        hashOrderId?: string;
        camperone?: {
          jsonConfig: any;
        };
        image?: string;
      } = {
        id: code,
        price: pricesPayload,
        profile: {
          countryId: getMarket(locale),
          languageId: getLang(locale),
        },
        size: selectedSize,
        sizeLabel,
        gender: productSheet.realTarget || productSheet.target || '',
        thumbnail: productSheet.imageBag || '', // en ocasiones queda como null y causa un error
      };
      if (voucherDiscount || voucher) {
        payload.voucher = voucher;
      }
      if (cookiesBagId) {
        payload.hashOrderId = decodeURI(cookiesBagId);
      }

      let camperOneJSON = null;
      if (isCamperOne || isRoku) {
        if (window.camperOneGetJson) {
          // old version camper one no wait
          try {
            camperOneJSON = window.camperOneGetJson();
          } catch (e) {
            // failed because jsonBO is undefined since
            // user comes from mobile and never customized (didn't load plugin)
            camperOneJSON = productSheet.camperone.jsonConfig;
          }

          payload.camperone = {
            jsonConfig: camperOneJSON,
          };

          addToCartHandler({
            payload,
            cookiesBagId,
            showJustAdded,
          });
        } else if (window.customDIM?.api) {
          // new dim version, wait for getJSON to finish
          try {
            window.customDIM.api.getCaptureAndJSON(
              512,
              512,
              'ffffff',
              async (data) => {
                const somePieceWithoutMaterial = data.piezas.some((item) => {
                  if (!['BORT', 'BORC', 'BOLT', 'BOLC'].includes(item?.idPieza)) {
                    return item?.material?.length === 0;
                  }

                  return false;
                });

                if (somePieceWithoutMaterial) {
                  // mostramos error rojo en plan talla
                  setCartError(t('ficha.producto', 'customize.required.pieces'));
                  setLoading(false);
                  return false;
                } else {
                  const viewUrl = data?.urlVistas;
                  const base64Image = data?.image;

                  data.urlVistas = [viewUrl];

                  if (base64Image) {
                    payload.image = base64Image;
                  }

                  delete data.image;

                  if (isRoku) {
                    const { piezas } = data;
                    const addMultiplePayload = piezas
                      .filter((pieza) => pieza?.customizable === true)
                      .map((item) => ({
                        sku: item.material[0].idMaterialEshop,
                        size: selectedSize,
                      }));

                    const addToCartMultipleResult = await window.addToCartMultiple(addMultiplePayload, true, bag);
                    const bagID = getCookie(COOKIES.BAG);

                    try {
                      const failed = !addToCartMultipleResult[0];
                      let succeededProducts = addToCartMultipleResult[1]?.filter((item) => item.status === 'OK');

                      if (failed) {
                        const failedProducts = addToCartMultipleResult[1].filter((item) => item.status !== 'OK');
                        const failedRokusWithInfo = [];
                        const getProductPromises = [];

                        failedProducts.forEach((failedItem) => {
                          getProductPromises.push(
                            getProduct({
                              context: undefined,
                              lang: locale,
                              productCode: failedItem.sku,
                              target: productSheet.realTarget || productSheet.target,
                            }).then((productInfo) => {
                              if (productInfo?.productSheet) {
                                failedRokusWithInfo.push(productInfo.productSheet);
                              }
                            }),
                          );
                        });

                        await Promise.all(getProductPromises);
                        // quitar del carrito los que hayan funcionado
                        // hacemos un get cart primero
                        // quitamos todos los que tengamos en la lista que coincidan

                        setFailedRokus(failedRokusWithInfo);

                        if (succeededProducts?.length > 0) {
                          const currentBag = await retrieveBag({
                            context: undefined,
                            payload: {
                              hashOrderId: bagID,
                              profile: {
                                countryId: market,
                                languageId: getLang(locale),
                              },
                            },
                          });

                          const productsCurrentlyInBag = currentBag?.shoppingCart;

                          if (productsCurrentlyInBag?.length > 0) {
                            const newBag = [...productsCurrentlyInBag].filter((item) => {
                              // devolver false para los que esten en succeeded
                              const successfulProduct = succeededProducts.find(
                                (succeededItem) => succeededItem.sku === item.productId && succeededItem.size === item.size,
                              );

                              if (successfulProduct !== undefined) {
                                // encontrado el producto, lo quitamos de la lista y filtramos
                                succeededProducts = succeededProducts.filter(
                                  (itemToCheck) => !(itemToCheck.sku === successfulProduct.sku && itemToCheck.size === successfulProduct.size),
                                );
                                return false;
                              }

                              return true;
                            });

                            const employeeId = getCookie(COOKIES.EMPLOYEE_ID) || '';
                            const zipCode = getCookie(COOKIES.ZIP) || '';
                            const tax = zipCode && zipCode !== '' ? { zipcode: zipCode } : {};

                            // FIXME: esta llamada es necesaria?
                            await getCart({
                              context: undefined,
                              lang: locale,
                              products: newBag,
                              hashOrderId: bagID,
                              employeeId,
                              bondValue: voucher,
                              tax,
                              withPaymentMethods: undefined,
                              cookies: undefined,
                              origin: undefined,
                            });
                          }
                        }

                        await handleGetRokuStock(selectedSize);
                      }

                      setShowRokuWarning(failed);

                      const updateForBag = [...bag, ...succeededProducts.map((item) => item.shoppingCart?.[0])];

                      setBag(updateForBag);
                      setBagLength(updateForBag.length);
                    } catch (e) {
                      console.error(e);
                    }

                    setLoading(false);
                  } else {
                    payload.camperone = {
                      jsonConfig: data,
                    };

                    addToCartHandler({
                      payload,
                      cookiesBagId,
                      showJustAdded,
                    });
                  }
                }
              },
              (error) => {
                console.error('error', error);
                throw error;
              },
            );
          } catch (e) {
            console.error('failed to get json from dim');
            console.error(e);
            camperOneJSON = productSheet.camperone.jsonConfig;
            payload.camperone = {
              jsonConfig: camperOneJSON,
            };
            addToCartHandler({
              payload,
              cookiesBagId,
              showJustAdded,
            });
          }
        }
      } else {
        // no wait
        addToCartHandler({
          payload,
          cookiesBagId,
          showJustAdded,
        });
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    });
    const urlParams = new URLSearchParams(window.location.search);
    const sizeValue = urlParams.get('size');
    const sizeIsAvailable = sizes && sizes.filter((size) => size.value === sizeValue && (size.available || notifyMe)).length > 0;
    if (sizeValue && sizeIsAvailable) {
      setSelectedSize(sizeValue);
      setSunneiSize(sizeValue);
    } else {
      setSelectedSize(defaultSelectedSize);
      setSunneiSize(defaultSelectedSize);
    }
  }, []);

  let sizeFound = selectedSize ? sizes.find((size) => size?.value === selectedSize) : {};
  if (!sizeFound) {
    sizeFound = {};
  }

  const isPresale = productSheet.presales;

  let addToCartLabel = !isPresale ? t('ficha.producto', 'add.to.bag', 'Add to bag') : t('ficha.producto', 'add.to.bag.presale', 'PRE-ORDER');

  if (isMembersOnly) {
    addToCartLabel = `${!isPresale ? t('ficha.producto', 'members.only.shop') : t('ficha.producto', 'add.to.bag.presale')} ${t('ficha.producto', 'members.only', '(Members Only)')}`;
  }

  const priceRange =
    prices?.priceDiscountFromFormatted && prices?.priceDiscountToFormatted ?
      `${prices.priceDiscountFromFormatted} - ${prices.priceDiscountToFormatted}`
    : `${prices?.priceFromFormatted} - ${prices?.priceToFormatted}`;

  const notifymeBySize = notifyMe && productSheet?.notifyMeType === 'SIZE';

  let shouldShowSizes = availablePrice;
  const hasSizeGuide = Array.isArray(productSheet.sizesGuide) && productSheet.sizesGuide.length > 0 && !storeData;
  let shouldShowButton =
    availablePrice &&
    (!productSheet.notifyMe || (productSheet.notifyMe && notifymeBySize && (availableSizes.includes(selectedSize) || selectedSize === undefined)));
  // si el producto tiene talla unica, es notifyMe por talla y no tiene disponibilidad, no debería mostrar nada
  if (productSheet.notifyMe && productSheet.notifyMeType === 'SIZE' && sizes.length === 1 && sizes.every((size) => !size.available)) {
    shouldShowSizes = false;
    shouldShowButton = false;
  }

  if (availableSizes.length === 0 && !productSheet?.notifyMe) {
    // NO es notifyme y no tiene disponibilidad de tallas, debería ser como si no tuviese precio
    shouldShowSizes = false;
    shouldShowButton = false;
  }

  const sizeGuideInfo = SIZE_GUIDE_WITH_MODELS.includes(productSheet.code) && productSheet.description ? productSheet.description : null;
  const isStandardNotifyMe = productSheet?.notifyMeType !== null && productSheet?.notifyMeType !== 'SIZE';
  const isBySizeWithNoSizes = !isStandardNotifyMe && availableSizes.length === 0;
  const hideFewStockMessage = isStandardNotifyMe || isBySizeWithNoSizes;
  const fixedPrice = getFixedPrice(market, profileData, prices?.current);

  useEffect(() => {
    try {
      if (skusWithoutStock !== null && isDimReady === true) {
        const emptyFunc = () => undefined;

        window.customDIM.api.setNoStockMaterials(skusWithoutStock, emptyFunc, emptyFunc);
      }
    } catch (e) {
      console.error('DIM error', e);
    }
  }, [skusWithoutStock, isDimReady]);

  const handleSelectRokuSize = async (size) => {
    setDefaultSelectedSize(size);
    await handleSelectSize(size);
    setShowSizeModal(false);
  };

  const countriesWithSizeSelector = ['US'];
  const manualYellowBox = Boolean(productSheet.textDiscountCollectionOld && !usrhashValue);
  const autoYellowBox = ![null, undefined, ''].includes(voucherPriceText);

  return (
    // <Col xs={columnSizes.xs} md={columnSizes.md} lg={columnSizes.lg}>
    <div className="relative flex h-full flex-col p-8 pt-2.5 lab:max-w-none lab:max-lg:px-2.5 md:mx-auto md:max-w-[260px] md:px-0 md:pt-8">
      {getDomainValue({
        camper:
          !isGiftCard ?
            <div className="mb-2.5 nnormal:hidden md:hidden">
              <ProductBrands id={productSheet.code} products={productSheet.brandProducts} camperlab={camperlab} />
              <ProductBreadcrumbs className="mt-3.5" variant="new" withCurrent={true} />
            </div>
          : null,
      })}
      <div className={`${styles.titleWrapper} max-md:mt-4 lab:max-md:mt-0`}>
        <>
          <h1 ref={titleRef} className={`scroll-m-14 ${styles.brandTitle} ${availablePrice ? '' : styles.brandTitleNotify}`}>
            {`${productSheet.brand} `}
            {productSheet.codeDescription ?
              <span
                dangerouslySetInnerHTML={{ __html: productSheet.codeDescription }}
                className={availablePrice || productSheet?.notifyMeType === 'OUT' ? styles.codeDescription : styles.codeDescriptionNotify}
              />
            : null}
          </h1>
          {getDomainValue({
            camper: null,
            camperlab: null,
            nnormal:
              productSheet?.codeFeatures ?
                <h2 dangerouslySetInnerHTML={{ __html: productSheet?.codeFeatures }} className={`${styles.codeDescription} ${styles.codeFeatures}`} />
              : null,
          })}
          {isGiftCard ?
            <div className="mt-[22px]">
              <ProductBrands id={productSheet.code} products={productSheet.brandProducts} camperlab={camperlab} />
            </div>
          : null}
          {(appmode || storeData) && <span className={styles.productSku}>{productSheet.code}</span>}
          {isCamperOne && !isRoku && <ONESVG className={styles.camperONESVG} />}
          {!isGiftCard && productSheet?.notifyMeType !== 'OUT' && (
            <>
              {getDomainValue({
                camper: recalculating && <Skeleton.Input active size="small" className={styles.recalcPrice} />,
                camperlab: null,
                nnormal: null,
              })}
              {!recalculating && (
                <p
                  className={availablePrice ? `${styles.productCurrentPrice} ${styles.priceSpan}` : styles.productCurrentPriceNotify}
                  data-ge-price="data-ge-price"
                  data-fp={fixedPrice}
                >
                  {/* es horrible y me sabe mal pero la lógica es funcional y es para ir rápido */}
                  {prices !== null && prices.pricePerSize ?
                    !availableSizes.includes(selectedSize) ?
                      priceRange
                    : sizeFound.price.currentFormated
                  : null}
                  {prices !== null && !prices.pricePerSize && productSheet.prices.currentFormated}
                </p>
              )}
              {!recalculating && availablePrice && productSheet.prices && (productSheet.prices.previous || productSheet.prices.priceDiscountFromFormatted) && (
                <p className={styles.discountPriceText}>
                  <del className={styles.discountPricePrevious} data-fp={getFixedPrice(market, profileData, productSheet?.prices?.previous)}>
                    {prices !== null && prices.pricePerSize ?
                      !availableSizes.includes(selectedSize) ?
                        `${prices.priceFromFormatted} - ${prices.priceToFormatted}`
                      : sizes.find((size) => size?.value === selectedSize)?.price?.previousFormated
                    : null}
                    {prices !== null && !prices.pricePerSize && productSheet.prices.previousFormated}
                  </del>
                  <span
                    className={`${getDomainValue({ camper: voucherApplied ? styles.discountFromVoucher : '', nnormal: '', camperlab: '' })} green`}
                  >{`${productSheet?.prices?.discountStr || `-${productSheet?.prices?.discount ?? productSheet?.prices?.discountLocal}%`}`}</span>
                </p>
              )}
              {!recalculating &&
                voucherApplied &&
                productSheet.prices?.previous !== undefined &&
                productSheet.prices?.previous > productSheet.prices?.current &&
                voucherPriceText === null && <p className={styles.voucherApplied}>{productSheet.prices.appliedLabel}</p>}
              {prices?.pricePerSize && <p className={styles.pricePerSize}>{t('listado.productos', 'final.price.acording', 'Final price acording to size')}</p>}
            </>
          )}
        </>
        {!isGiftCard ?
          <>
            {getDomainValue({
              camper: market === 'AU' && availablePrice && <AfterpayButton totalPrice={totalPrice} currency={prices.currency || 'AUD'} />,
              camperlab: null,
              nnormal: null,
            })}
            {!camperlab && BAZAAR_VOICE_MARKETS.includes(market) && (
              <div id="product_rating" className={styles.productRating}>
                <div data-bv-show="rating_summary" data-bv-product-id={productSheet?.code}></div>
              </div>
            )}
            {availablePrice && reviewsCount > 0 && !productSheet.recycled && !productSheet.camperlab && (
              <div className={styles.rateWrapper} onClick={onClickReviews}>
                <Rate className={styles.rateStars} allowHalf defaultValue={productSheet.rate} value={averageRate} disabled character={<RateSVG />} />
                <p className="relative -top-0.5 mb-3 ml-2.5 inline-block cursor-pointer text-xs font-normal underline">{`${reviewsCount} ${t('ficha.producto', 'see.reviews')}`}</p>
              </div>
            )}
          </>
        : <GiftCard prices={productSheet.prices} locale={locale} productSheet={productSheet} />}
      </div>
      {!isGiftCard ?
        <div className="mt-[22px] camper:max-md:hidden">
          <ProductBrands id={productSheet.code} products={productSheet.brandProducts} camperlab={camperlab} />
        </div>
      : null}
      {/* productSheet.textDiscountCollection &&
            <p className={recalculating ? `${styles.textDiscount} subclaimcta` : styles.textDiscount} dangerouslySetInnerHTML={{ __html: productSheet.textDiscountCollection }} />
          */}
      {getDomainValue({
        nnormal:
          hasMidsoles ?
            <KboixMidsoles
              midsoles={productSheet.kboixData.midsoles}
              selected={selectedMidsoles}
              setSelected={setSelectedMidsoles}
              selectedSize={selectedSize}
              canConfig={productSheet.kboixData.canConfig}
              min={productSheet.kboixData.minExtra}
            />
          : null,
      })}
      {productSheet?.notifyMeType !== 'OUT' && manualYellowBox && !autoYellowBox && (
        <p className={styles.textDiscount} dangerouslySetInnerHTML={{ __html: productSheet.textDiscountCollectionOld }} />
      )}
      {productSheet?.notifyMeType !== 'OUT' && !recalculating && autoYellowBox && (
        <p className={styles.textDiscount} dangerouslySetInnerHTML={{ __html: voucherPriceText }} />
      )}
      <TargetLinks className="mt-6" />
      <Row id="row-sizes">
        <Col span={24}>
          <div id="sizes">
            <div
              data-sizes={shouldShowSizes && !isUniqueSize}
              className="my-[22px] flex items-center justify-between data-[sizes=false]:mb-0 data-[sizes=false]:justify-end empty:hidden nnormal:my-4"
            >
              {/* En camperlab la han liado y metido todo con las tallas de US por eso hay que hacer la comprobación para que si es camperlab, apparel y pantalones mostrar (US) en vez del pais correspondiente */}
              {getDomainValue({
                camper:
                  !isUniqueSize ?
                    <h3
                      className="mb-0 text-sm [@media(min-width:1440px)]:text-xs"
                      dangerouslySetInnerHTML={{
                        __html: `${t('ficha.producto', `elige.talla.${productSheet.realTarget}`, `***Choose your ${productSheet.realTarget} size`)} (${productSheet.camperlab && productSheet.family === String(FAMILIES.CLOTHES) && ['trousers', 'shorts'].includes(productSheet.sizesGuide?.[0]?.productType) ? 'US' : country})`,
                      }}
                    />
                  : null,
              })}
              {hasSizeGuide ?
                <SizesGuide
                  target={productSheet.realTarget}
                  code={code}
                  name={productSheet?.brand?.toLowerCase()}
                  guide={productSheet.sizesGuide}
                  currentCountry={country}
                  sizeGuideInfo={sizeGuideInfo}
                />
              : null}
            </div>
          </div>
          {shouldShowSizes && !isUniqueSize && (
            <>
              <SizesList
                isCamperOne={isCamperOne}
                sizes={sizes}
                defaultSelectedSize={defaultSelectedSize}
                onChange={handleSelectSize}
                isSock={isSock}
                showNotifyMe
                locale={locale}
                code={code}
                select={getDomainValue({
                  camper: countriesWithSizeSelector.includes(market) && [FAMILIES.SHOES, FAMILIES.ACCESSORIES].includes(Number(productSheet.family)),
                  nnormal: true,
                  camperlab: countriesWithSizeSelector.includes(market) && [FAMILIES.SHOES, FAMILIES.ACCESSORIES].includes(Number(productSheet.family)),
                })}
                currentCountry={country}
                productSheet={productSheet}
                showLimitedSizeStyle={undefined}
                onMouseOverSize={undefined}
                onMouseOutSize={undefined}
                calculateColumns={undefined}
              />

              {
                getDomainValue({
                  camper: !isCamperOne && limitedSizes.includes(selectedSize) && !hideFewStockMessage && (
                    <p className={styles.limitedSizesLabel}>* {t('ficha.producto', 'size.info.only.few')}</p>
                  ),
                }) as ReactNode | null
              }

              {
                getDomainValue({
                  camper: !isCamperOne && limitedSizes?.length > 0 && (!availableSizes.includes(selectedSize) || !limitedSizes.includes(selectedSize)) && (
                    <p className={styles.limitedSizesLabel}>* {t('ficha.producto', 'size.info.few.standby')}</p>
                  ),
                }) as ReactNode | null
              }
            </>
          )}
        </Col>
      </Row>
      <div className={styles.stickyCol} id="sticky-col">
        {shouldShowButton && !isGiftCard ?
          <div className={styles.addToCartBtn} ref={ref}>
            {cartError && <p className={styles.cartErrorText}>{cartError}</p>}
            {isFullScreen === false && (
              <AddToBagBtn
                id="add-to-cart"
                selectedSize={selectedSize}
                disabled={isBuyingDisabled}
                loading={loading === true}
                onMouseOver={() => !isBuyingDisabled && setIsMouseOverCartBtn(true)}
                onMouseLeave={() => !isBuyingDisabled && setIsMouseOverCartBtn(false)}
                onClick={() => handleClickAddToCartBtn(true)}
                className={isMouseOverCartBtn && (!availableSizes.includes(selectedSize) || !availablePrice) ? styles.addToCartBtnDisabled : ''}
                label={
                  isMouseOverCartBtn && (!availableSizes.includes(selectedSize) || !availablePrice) ? t('ficha.producto', 'elije.tu.talla') : addToCartLabel
                }
                market={market}
                setLoading={setLoading}
                extraData={productSheet.extraData}
                voucherApplied={voucherApplied}
              />
            )}
          </div>
        : !isGiftCard && (
            <>
              {productSheet.notifyMe && !productSheet.recycled && availablePrice && (
                <div className={styles.notifyMe}>
                  {t(
                    'ficha.producto',
                    `avisame.texto.${productSheet.notifyMeType}`,
                    '***Este producto no esta a la venta en este momento, pero podemos avisarte por email en cuanto este disponible',
                  )}
                </div>
              )}
              {!productSheet.recycled && notifyMe && availablePrice ?
                <NotifyMeButton defaultSize={selectedSize} code={code} productSheet={productSheet} camperlab={camperlab} />
              : <div className={styles.noStock}>{t('ficha.producto', 'sin.stock')}</div>}
            </>
          )
        }
        {shouldShowButton && !isGiftCard && (
          <div
            id="floating-add-to-cart"
            className={`invisible fixed bottom-0 left-0 z-[100] w-full bg-neutral opacity-0 transition-opacity duration-300 md:hidden lab:md:block lab:lg:hidden ${styles.addToCartBtnFixed} ${!inView ? `${styles.visible}` : ''}`}
          >
            {/* {cartError && <p className={styles.cartErrorText}>{cartError}</p>} */}
            {isFullScreen === false && (
              <AddToBagBtn
                id="floating-button"
                floatingButton
                selectedSize={selectedSize}
                disabled={isBuyingDisabled}
                loading={loading === true}
                onMouseOver={() => !isBuyingDisabled && setIsMouseOverCartBtn(true)}
                onMouseLeave={() => !isBuyingDisabled && setIsMouseOverCartBtn(false)}
                onClick={() => handleClickAddToCartBtn(true)}
                className={isMouseOverCartBtn && (!availableSizes.includes(selectedSize) || !availablePrice) ? styles.addToCartBtnDisabled : ''}
                label={!availableSizes.includes(selectedSize) || !availablePrice ? t('ficha.producto', 'elije.tu.talla') : addToCartLabel}
                market={market}
                setLoading={setLoading}
                extraData={productSheet.extraData}
                voucherApplied={voucherApplied}
              />
            )}
          </div>
        )}
        {/* {!IS_NNORMAL && !profileData.global_e && !appmode && !storeData && (
            <AddToWishlistBtn
              product={{
                productId: code,
                size: selectedSize || '',
                profile: {
                  countryId: getMarket(locale),
                  languageId: getLang(locale),
                },
              }}
              productSheet={productSheet}
              isCamperOne={isCamperOne}
            />
          )} */}
        {/* {IS_CAMPER ?
            <ProductShare baseUrl={productSheet.baseUrl} code={productSheet.code} image={productSheet.image} />
          : null} */}
        {getDomainValue({
          camper: (
            <div className="mt-3">
              <DispoModal productSheet={productSheet} />
            </div>
          ),
        })}
        {!availablePrice && reviewsCount > 0 && !productSheet.recycled && !productSheet.camperlab && (
          <div className={`${styles.rateWrapper} ${styles.rateWrapperNotify}`} onClick={onClickReviews}>
            <Rate className={styles.rateStars} allowHalf defaultValue={productSheet.rate} value={averageRate} disabled character={<RateSVG />} />
            <h3 className="relative -top-0.5 mb-3 ml-2.5 inline-block cursor-pointer text-xs font-normal underline">{`${reviewsCount} ${t('ficha.producto', 'see.reviews')}`}</h3>
          </div>
        )}
        <ProductInfoSection />
        {/* @ts-ignore Antd!! */}
        <Modal visible={showRokuWarning} closable={false} footer={null} width={875} wrapClassName={styles.rokuWarningModal}>
          <div className={styles.modalText}>
            <h2>
              <b>{t('ficha.producto', 'we.are.sorry', '***¡Lo sentimos!')}</b>
            </h2>
            <h3>{t('ficha.producto', 'roku.failed.add.to.bag', '***Alguno de los productos no se han podido añadir a la bolsa.')}</h3>
            <div className={styles.failedRokusWrapper}>
              {failedRokus.map((failedItem) => (
                <ProductCard
                  locale={locale}
                  key={`failed-piece-${failedItem.id}`}
                  withSKU
                  squareImg
                  {...failedItem}
                  images={{ sources: [{ main: failedItem.imageBag }] }}
                  withHover={false}
                  showPrices={false}
                />
              ))}
            </div>
            <Button onClick={() => setShowRokuWarning(false)} type="primary">
              {t('ficha.producto', 'seguir.customizando')}
            </Button>
          </div>
        </Modal>
        {/* @ts-ignore Antd!! */}
        <Modal visible={showSunneiWarning} closable={false} footer={null} width={875} wrapClassName={styles.rokuWarningModal}>
          <div className={styles.modalText}>
            <h2>
              <b>{t('ficha.producto', 'we.are.sorry', '***¡Lo sentimos!')}</b>
            </h2>
            <h3>{t('ficha.producto', 'roku.failed.add.to.bag', '***Alguno de los productos no se han podido añadir a la bolsa.')}</h3>
            <div className={styles.failedRokusWrapper}>
              {failedSunneis.map((failedItem) => (
                <ProductCard
                  locale={locale}
                  key={`failed-piece-${failedItem.id}`}
                  withSKU
                  squareImg
                  {...failedItem}
                  images={{ sources: [{ main: failedItem.imageBag }] }}
                  withHover={false}
                  showPrices={false}
                  sizes={[]}
                />
              ))}
            </div>
            <Button onClick={() => setShowSunneiWarning(false)} type="primary">
              {t('ficha.producto', 'seguir.customizando')}
            </Button>
          </div>
        </Modal>
        {/* @ts-ignore Antd! */}
        <Modal visible={showSizeModal} closable={true} footer={null} width={400} onCancel={() => setShowSizeModal(false)} wrapClassName={styles.sizesModal}>
          <h2>{t('ficha.producto', 'sizes.modal.title')}</h2>
          <div className={styles.sizeListModal}>
            <SizesList
              isCamperOne={isCamperOne}
              sizes={sizes}
              defaultSelectedSize={defaultSelectedSize}
              onChange={handleSelectRokuSize}
              isSock={isSock}
              showNotifyMe={false}
              locale={locale}
              code={code}
              select={false}
              currentCountry={country}
              productSheet={productSheet}
              showLimitedSizeStyle={undefined}
              onMouseOverSize={undefined}
              onMouseOutSize={undefined}
              calculateColumns={undefined}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
}
